
import { objectRecurseImmutable } from ".."
import { modelFactory } from "../common/model_factory"
import { isEmpty, safeJsonParse, safeJsonStringify } from "./base_error.utils"

export type ErrorScope = `PUBLIC` | `SYSTEM_ONLY` | `INTERNAL_SERVER` | `DEBUG_ONLY`

// eslint-disable-next-line @typescript-eslint/ban-types
type ExcludeMethod<T> = Pick<T, { [K in keyof T]: T[K] extends Function ? never : K }[keyof T]>

type DebugType<O = Record<string, unknown>> = {
  [K in keyof O]?: O[K]
}
type AnyObject = (Record<string, any>) & (Record<string, any> | object)
type AnyValue = AnyObject | string | number | boolean | symbol | bigint

const hasProperty = Object.prototype.hasOwnProperty

export class BaseError<D=unknown>
  extends Error {
  public name = `ApplicationError`
  public debug?: AnyValue
  protected scope: ErrorScope = `INTERNAL_SERVER`

  constructor(
    construct: string | Partial<ExcludeMethod<BaseError>>,
    debug?: DebugType<D>,
    override?: Partial<ExcludeMethod<BaseError>>,
    public message = `error`,
    public public_message?: string,
    public stack?: string,
    public error?: Error | BaseError | Record<string, any>,
    public cause?: any, // Because different TS version always change the cause type. use `any` type instead
    public code?: number,
    public method?: string,
    public expose = false,
  ) {
    super()
    if (!isEmpty(construct) && typeof construct === `object`) {
      for (const key in construct) {
        if (hasProperty.call(construct, key)) {
          this[key] = construct[key]
          delete construct[key]
        }
      }
    } else if (!isEmpty(construct)) {
      this.message = String(construct)
    }

    if (!isEmpty(override) && typeof override === `object`) {
      for (const key in override) {
        if (hasProperty.call(override, key)) {
          this[key] = override[key]
          delete override[key]
        }
      }

      override = void 0
    }

    if (debug instanceof Error) {
      this.error = debug
      debug = void 0
    } else if (typeof debug === `object`) {
      try {
        const visit = debugObject => {
          objectRecurseImmutable({
            target: debugObject,
            doOnObject: (target, key, owner: any, depth) => {
              owner = owner as AnyObject
              if (!owner)
                return owner
              if ((owner)?.error)
                return owner
              if (owner[key] instanceof Error) {
                this.error = owner[key]
                delete owner[key]
              }

              return target
            },
          }, 12)
        }

        visit(debug)
      } catch {
        // do nothing
      }
    }

    try {
      const nonCircular: any = safeJsonParse(debug)
      nonCircular ? this.debug = nonCircular : void 0
    } catch {
      void 0
    }

    if (!this.message && this.error?.message)
      this.message = this.error.message
    if (!this.public_message && this.message && this.scope === `PUBLIC`)
      this.public_message = this.message
  }

  public toObject() {
    return safeJsonParse(this)!
  }

  public toString() {
    return safeJsonStringify(this)
  }

  public makePublic() {
    this.scope = `PUBLIC`
    if (this.message && !this.public_message)
      this.public_message = this.message
    this.expose = true
    return this
  }

  setScope(scope: ErrorScope) {
    this.scope = scope
    if (scope === `PUBLIC`)
      this.makePublic()
    return this
  }

  get getScope() {
    return this.scope
  }

  public rename(name?: string) {
    if (name)
      this.name = name

    return this
  }

  public static factory = modelFactory(BaseError, {
    allAutoGenerated: true,
  })
}
